import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.qrmenu.rw/api';

// Helper function to get proper image URL
const getImageUrl = (imageUrl: string | null): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
    }
    // Uploads are served at domain root /uploads, not /api/uploads
    // Remove trailing /api from the API_URL
    const baseUrl = API_URL.replace(/\/api$/, '');
    return `${baseUrl}${imageUrl}`;
};

interface Establishment {
    name: string;
    logo_url: string | null;
}

interface SocialMedia {
    id: number;
    platform: string;
    url: string;
    is_active: boolean;
}

interface PaymentMethod {
    id: number;
    name: string;
    type: string;
    ussd_prefix: string | null;
    merchant_code: string | null;
    ussd_code: string | null;
    account_number: string | null;
    account_name: string | null;
    instructions: string | null;
    is_active: boolean;
}

const LandingPage: React.FC = () => {
    const { establishmentId, tableId } = useParams<{ establishmentId: string; tableId: string }>();
    const history = useHistory();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [establishment, setEstablishment] = useState<Establishment | null>(null);
    const [tableName, setTableName] = useState<string>('');
    const [socialMedia, setSocialMedia] = useState<SocialMedia[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [loading, setLoading] = useState(true);
    const [callingWaiter, setCallingWaiter] = useState(false);
    const [waiterCalled, setWaiterCalled] = useState(false);

    // Booking verification state
    const [isTableBooked, setIsTableBooked] = useState(false);
    const [bookingVerified, setBookingVerified] = useState(false);
    const [bookingCode, setBookingCode] = useState('');
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [bookingInfo, setBookingInfo] = useState<{
        customer_name: string;
        time_slot_start: string;
        time_slot_end: string;
    } | null>(null);

    // Generate or retrieve session ID for scan tracking
    const getSessionId = (): string => {
        let sessionId = sessionStorage.getItem('qr_session_id');
        if (!sessionId) {
            sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
            sessionStorage.setItem('qr_session_id', sessionId);
        }
        return sessionId;
    };

    // Check if session already has verified booking code for this table
    const getVerifiedBookingKey = () => `booking_verified_${tableId}`;

    // Track QR code scan
    const trackScan = async () => {
        try {
            await axios.post(`${API_URL}/public/track-scan`, {
                tableId,
                sessionId: getSessionId()
            });
        } catch (error) {
            // Don't interrupt user experience if tracking fails
            console.debug('Scan tracking skipped');
        }
    };

    // Check booking status of table
    const checkTableBookingStatus = async () => {
        try {
            const response = await axios.get(`${API_URL}/bookings/public/table-status/${tableId}`);
            if (response.data.is_booked) {
                setIsTableBooked(true);
                setBookingInfo({
                    customer_name: response.data.booking?.customer_name || '',
                    time_slot_start: response.data.booking?.time_slot_start || '',
                    time_slot_end: response.data.booking?.time_slot_end || ''
                });

                // Check if already verified in this session
                const verifiedCode = sessionStorage.getItem(getVerifiedBookingKey());
                if (verifiedCode) {
                    setBookingVerified(true);
                }
            }
        } catch (error) {
            // If API fails, assume no booking requirement
            console.debug('Booking status check skipped');
        }
    };

    // Verify booking code
    const handleVerifyCode = async () => {
        if (!bookingCode.trim()) {
            showToast('Please enter your booking code', 'warning');
            return;
        }

        try {
            setVerifyingCode(true);
            const response = await axios.post(`${API_URL}/bookings/public/verify-code`, {
                table_id: parseInt(tableId),
                booking_code: bookingCode.toUpperCase().trim()
            });

            if (response.data.valid) {
                setBookingVerified(true);
                sessionStorage.setItem(getVerifiedBookingKey(), bookingCode);
                showToast('Welcome! Enjoy your visit.', 'success');
            } else {
                showToast('Invalid booking code. Please try again.', 'error');
            }
        } catch (error) {
            showToast((error && error.response && error.response.data && error.response.data.error) || 'Failed to verify code', 'error');
        } finally {
            setVerifyingCode(false);
        }
    };

    useEffect(() => {
        fetchData();
        trackScan();
        checkTableBookingStatus();
    }, [establishmentId, tableId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch menu data first (required)
            try {
                const menuRes = await axios.get(`${API_URL}/public/menu/${establishmentId}/table/${tableId}`);
                setTableName(menuRes.data.table.name);
                setEstablishment(menuRes.data.establishment);
                document.body.dataset.theme = menuRes.data.establishment?.client_theme || 'royal-dark';
            } catch (error) {
                console.error('Error fetching menu data:', error);
            }

            // Fetch social media and payment methods independently so one failure doesn't break the other
            try {
                const socialRes = await axios.get(`${API_URL}/public/${establishmentId}/social-media`);
                setSocialMedia(socialRes.data.filter((sm: SocialMedia) => sm.is_active));
            } catch (error) {
                console.error('Error fetching social media:', error);
            }

            try {
                const paymentRes = await axios.get(`${API_URL}/public/${establishmentId}/payment-methods`);
                setPaymentMethods(paymentRes.data.filter((pm: PaymentMethod) => pm.is_active));
            } catch (error) {
                console.error('Error fetching payment methods:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCallWaiter = async () => {
        try {
            setCallingWaiter(true);
            await axios.post(`${API_URL}/public/call-waiter`, {
                tableId,
                timestamp: new Date().toISOString()
            });
            setWaiterCalled(true);
            showToast(t('landing.waiterNotified', 'Waiter has been notified!'), 'success');
            setTimeout(() => setWaiterCalled(false), 5000); // Reset after 5 seconds
        } catch (error) {
            console.error('Error calling waiter:', error);
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                // Already a pending waiter call
                showToast(t('landing.waiterAlreadyCalled', 'A waiter has already been called for your table. Please wait for assistance.'), 'warning', 5000);
                setWaiterCalled(true);
                setTimeout(() => setWaiterCalled(false), 5000);
            } else {
                showToast(t('landing.waiterError', 'Failed to call waiter. Please try again.'), 'error');
            }
        } finally {
            setCallingWaiter(false);
        }
    };

    const getSocialIcon = (platform: string) => {
        const iconStyle = { width: '40px', height: '40px', fill: 'white' };

        switch (platform.toLowerCase()) {
            case 'facebook':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" /></svg>;
            case 'twitter':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" fill="#1DA1F2" /></svg>;
            case 'instagram':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" fill="url(#instagram-gradient)" /><defs><radialGradient id="instagram-gradient"><stop offset="0%" stopColor="#f9ed32" /><stop offset="50%" stopColor="#ee2a7b" /><stop offset="100%" stopColor="#002aff" /></radialGradient></defs></svg>;
            case 'linkedin':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2" /></svg>;
            case 'youtube':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000" /></svg>;
            case 'tiktok':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" fill="#000000" /></svg>;
            case 'whatsapp':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="#25D366" /></svg>;
            case 'telegram':
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" fill="#26A5E4" /></svg>;
            default:
                return <svg style={iconStyle} viewBox="0 0 24 24"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill="white" /></svg>;
        }
    };

    if (loading) {
        return (
            <div className="landing-page landing-page--centered" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
                color: 'white'
            }}>
                <div className="glass" style={{ padding: '2rem', borderRadius: '1rem' }}>
                    Loading...
                </div>
            </div>
        );
    }

    // Show booking code entry if table is booked but not yet verified
    if (isTableBooked && !bookingVerified) {
        return (
            <div className="landing-page landing-page--centered" style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
                padding: '2rem 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    maxWidth: '400px',
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '1.5rem',
                    padding: '2rem',
                    textAlign: 'center'
                }}>
                    {establishment?.logo_url && (
                        <img
                            src={getImageUrl(establishment.logo_url) || ''}
                            alt={establishment.name}
                            style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'contain',
                                margin: '0 auto 1rem',
                                borderRadius: '15px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                padding: '0.5rem'
                            }}
                        />
                    )}

                    <div style={{
                        width: '60px',
                        height: '60px',
                        margin: '0 auto 1rem',
                        background: 'rgba(255, 215, 0, 0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="#FFD700" style={{ width: '28px', height: '28px' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                        </svg>
                    </div>

                    <h2 style={{ color: 'white', marginBottom: '0.5rem' }}>
                        Reserved Table
                    </h2>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1.5rem' }}>
                        {tableName} is reserved. Please enter your booking code to continue.
                    </p>

                    <div style={{ marginBottom: '1rem' }}>
                        <input
                            type="text"
                            value={bookingCode}
                            onChange={(e) => setBookingCode(e.target.value.toUpperCase())}
                            placeholder="Enter booking code"
                            maxLength={6}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                fontSize: '1.5rem',
                                textAlign: 'center',
                                letterSpacing: '0.25em',
                                background: 'rgba(0, 0, 0, 0.3)',
                                border: '2px solid rgba(255, 215, 0, 0.3)',
                                borderRadius: '0.75rem',
                                color: '#FFD700',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleVerifyCode();
                                }
                            }}
                        />
                    </div>

                    <button
                        onClick={handleVerifyCode}
                        disabled={verifyingCode || !bookingCode.trim()}
                        style={{
                            width: '100%',
                            padding: '1rem',
                            background: bookingCode.trim()
                                ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                                : 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            borderRadius: '0.75rem',
                            color: bookingCode.trim() ? '#0a0a0a' : 'rgba(255, 255, 255, 0.5)',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            cursor: bookingCode.trim() ? 'pointer' : 'not-allowed',
                            opacity: verifyingCode ? 0.7 : 1
                        }}
                    >
                        {verifyingCode ? 'Verifying...' : 'Verify Code'}
                    </button>

                    <p style={{
                        color: 'rgba(255, 255, 255, 0.4)',
                        fontSize: '0.8rem',
                        marginTop: '1.5rem'
                    }}>
                        Don't have a booking code?<br />
                        Please ask the staff for assistance.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="landing-page" style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
            padding: '2rem 1rem'
        }}>
            <LanguageSwitcher />
            {/* Logo and Establishment Name */}
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
                {establishment?.logo_url && (
                    <img
                        src={getImageUrl(establishment.logo_url) || ''}
                        alt={establishment.name}
                        style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'contain',
                            margin: '0 auto 1.5rem',
                            borderRadius: '20px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            padding: '1rem'
                        }}
                    />
                )}
                <h1 style={{
                    color: 'white',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '0.5rem'
                }}>
                    {t('landing.welcome')} {establishment?.name}
                </h1>
                <div style={{
                    background: 'rgba(139, 0, 0, 0.2)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.75rem',
                    display: 'inline-block',
                    marginTop: '1rem'
                }}>
                    <p style={{ color: 'white', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                        {tableName || 'Table'}
                    </p>
                </div>
            </div>

            {/* Menu Button */}
            <div style={{ maxWidth: '600px', margin: '0 auto', marginBottom: '2rem' }}>
                <button
                    onClick={() => history.push(`/${establishmentId}/order/${tableId}`)}
                    className="glass-dark"
                    style={{
                        width: '100%',
                        padding: '1.5rem',
                        borderRadius: '1rem',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        marginBottom: '1.5rem'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            fontSize: '3rem',
                            background: 'rgba(139, 0, 0, 0.2)',
                            borderRadius: '12px',
                            width: '70px',
                            height: '70px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            📋
                        </div>
                        <div style={{ flex: 1, textAlign: 'left' }}>
                            <h3 style={{ color: 'white', fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                                {t('landing.viewMenu')}
                            </h3>
                            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                {t('landing.menuDescription')}
                            </p>
                        </div>
                        <div style={{ color: 'rgba(139, 0, 0, 1)', fontSize: '1.5rem' }}>→</div>
                    </div>
                </button>

                {/* Call Waiter Button */}
                <button
                    onClick={handleCallWaiter}
                    disabled={callingWaiter || waiterCalled}
                    className="glass-dark"
                    style={{
                        width: '100%',
                        padding: '1.5rem',
                        borderRadius: '1rem',
                        border: 'none',
                        cursor: waiterCalled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        marginBottom: '1.5rem',
                        opacity: waiterCalled ? 0.7 : 1
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            fontSize: '3rem',
                            background: waiterCalled ? 'rgba(0, 139, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)',
                            borderRadius: '12px',
                            width: '70px',
                            height: '70px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {waiterCalled ? '✅' : '🔔'}
                        </div>
                        <div style={{ flex: 1, textAlign: 'left' }}>
                            <h3 style={{ color: 'white', fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                                {waiterCalled ? t('landing.waiterCalled') : callingWaiter ? t('common.loading') : t('landing.callWaiter')}
                            </h3>
                            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                                {waiterCalled ? t('landing.waiterCalledDesc') : t('landing.needHelp')}
                            </p>
                        </div>
                        {!waiterCalled && <div style={{ color: 'rgba(255, 165, 0, 1)', fontSize: '1.5rem' }}>→</div>}
                    </div>
                </button>

                {/* Social Media Section */}
                {socialMedia.length > 0 && (
                    <div className="glass-dark" style={{
                        padding: '1.5rem',
                        borderRadius: '1rem',
                        marginBottom: '1.5rem'
                    }}>
                        <h3 style={{
                            color: 'white',
                            fontSize: '1.25rem',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <span>🌐</span> {t('landing.followUs')}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.75rem' }}>
                            {socialMedia.map(sm => (
                                <a
                                    key={sm.id}
                                    href={sm.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="glass"
                                    style={{
                                        padding: '1rem',
                                        borderRadius: '0.75rem',
                                        textDecoration: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    <div style={{
                                        width: '50px',
                                        height: '50px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {getSocialIcon(sm.platform)}
                                    </div>
                                    <span style={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize'
                                    }}>
                                        {sm.platform}
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Payment Methods Section */}
                {paymentMethods.length > 0 && (
                    <div className="glass-dark" style={{
                        padding: '1.5rem',
                        borderRadius: '1rem'
                    }}>
                        <h3 style={{
                            color: 'white',
                            fontSize: '1.25rem',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <span>💳</span> {t('landing.payments')}
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {paymentMethods.map(pm => {
                                // Construct USSD code from prefix and merchant code
                                const ussdCode = pm.ussd_prefix && pm.merchant_code
                                    ? `${pm.ussd_prefix}${pm.merchant_code}#`
                                    : pm.ussd_code;

                                // Encode USSD code for tel: links (* -> %2A, # -> %23)
                                const telLink = ussdCode
                                    ? `tel:${encodeURIComponent(ussdCode)}`
                                    : undefined;

                                return (
                                    <a
                                        key={pm.id}
                                        href={telLink}
                                        onClick={(e) => {
                                            if (!ussdCode) {
                                                e.preventDefault();
                                            }
                                        }}
                                        className="glass"
                                        style={{
                                            padding: '1rem',
                                            borderRadius: '0.75rem',
                                            textDecoration: 'none',
                                            cursor: ussdCode ? 'pointer' : 'default',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: 'white', fontWeight: 'bold' }}>
                                                {pm.name}
                                            </span>
                                            {ussdCode && (
                                                <span style={{
                                                    color: 'rgba(139, 0, 0, 1)',
                                                    fontSize: '0.875rem',
                                                    fontFamily: 'monospace',
                                                    background: 'rgba(139, 0, 0, 0.1)',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '0.25rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem'
                                                }}>
                                                    {ussdCode} <span style={{ fontSize: '1rem' }}>📞</span>
                                                </span>
                                            )}
                                        </div>
                                        {pm.account_number && (
                                            <div style={{ marginTop: '0.5rem' }}>
                                                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem', margin: 0 }}>
                                                    {pm.account_name && <span>{pm.account_name}<br /></span>}
                                                    <span style={{ fontFamily: 'monospace' }}>{pm.account_number}</span>
                                                </p>
                                            </div>
                                        )}
                                        {pm.instructions && (
                                            <div style={{ marginTop: '0.5rem' }}>
                                                <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', margin: 0, fontStyle: 'italic' }}>
                                                    {pm.instructions}
                                                </p>
                                            </div>
                                        )}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LandingPage;
